/* Skio subscription picker — Impact theme
 * Writes selling_plan into the product form, keeps state across Impact's
 * variant re-renders, and optionally syncs the theme's main price block.
 */
(() => {
  if (customElements.get('subscription-picker')) return;

  // State survives Impact re-rendering the Custom Liquid block on variant change
  const STATE = (window.__ggSubPicker = window.__ggSubPicker || {});

  class SubscriptionPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('section-id') || 'default';
      this.formId = this.getAttribute('form-id');
      this.input = this.querySelector('[data-selling-plan-input]');
      this.radios = Array.from(this.querySelectorAll('[data-mode-radio]'));
      this.intervals = Array.from(this.querySelectorAll('[data-plan-id]'));
      this.cards = Array.from(this.querySelectorAll('[data-mode-card]'));
      this.perPlan = Array.from(this.querySelectorAll('[data-plan]'));
      this.badge = this.querySelector('[data-badge]');

      this._resolveForm();

      const saved = STATE[this.sectionId] || {};
      this.mode = saved.mode || this.getAttribute('default-mode') || 'subscribe';

      const valid = (id) => !!id && this.intervals.some((b) => b.dataset.planId === id);
      let planId = saved.planId;
      if (!valid(planId)) planId = this.getAttribute('default-plan-id');
      if (!valid(planId)) planId = this.intervals[0] ? this.intervals[0].dataset.planId : null;
      this.planId = planId;

      this.radios.forEach((r) => r.addEventListener('change', () => this.setMode(r.value)));
      this.intervals.forEach((b) => b.addEventListener('click', () => this.setPlan(b.dataset.planId)));

      this.render();
      // Price block may be re-rendered after this element on variant change
      requestAnimationFrame(() => setTimeout(() => this.render(), 60));
    }

    _resolveForm() {
      let form = this.formId ? document.getElementById(this.formId) : null;
      if (!form) {
        const section = this.closest('.shopify-section') || document;
        form =
          section.querySelector('buy-buttons form[action*="/cart/add"]') ||
          section.querySelector('product-form form[action*="/cart/add"]') ||
          section.querySelector('form[action*="/cart/add"]:not([id*="payment-installment"])');
        if (form && form.id) this.formId = form.id;
      }
      if (this.input && this.formId) this.input.setAttribute('form', this.formId);
    }

    setMode(mode) {
      this.mode = mode;
      this.render();
    }

    setPlan(planId) {
      this.planId = planId;
      this.mode = 'subscribe';
      this.render();
    }

    get active() {
      return this.intervals.find((b) => b.dataset.planId === this.planId) || null;
    }

    render() {
      const sub = this.mode === 'subscribe' && !!this.planId;
      STATE[this.sectionId] = { mode: this.mode, planId: this.planId };

      this.radios.forEach((r) => (r.checked = r.value === this.mode));
      this.cards.forEach((c) => c.toggleAttribute('data-selected', c.dataset.modeCard === this.mode));
      this.intervals.forEach((b) => {
        const on = b.dataset.planId === this.planId;
        b.toggleAttribute('data-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.perPlan.forEach((el) => (el.hidden = el.dataset.plan !== this.planId));

      const active = this.active;
      if (this.badge && active && active.dataset.percent) {
        this.badge.textContent = `Save ${active.dataset.percent}%`;
      }

      if (this.input) {
        const next = sub ? this.planId : '';
        if (this.input.value !== next) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (this.hasAttribute('sync-price')) this._syncPrice(sub);

      this.dispatchEvent(
        new CustomEvent('subscription-picker:change', {
          bubbles: true,
          detail: { mode: this.mode, sellingPlanId: sub ? this.planId : null }
        })
      );
    }

    _syncPrice(sub) {
      const section = this.closest('.shopify-section') || document;
      const list = section.querySelector('[data-block-type="price"] price-list');
      if (!list) return;
      const sale = list.querySelector('sale-price');
      if (!sale) return;

      if (!sale.dataset.onetimeHtml) sale.dataset.onetimeHtml = sale.innerHTML;
      let compare = list.querySelector('compare-at-price[data-sub-compare]');

      if (sub) {
        const priceEl = this.querySelector(`[data-plan-price][data-plan="${this.planId}"]`);
        if (!priceEl) return;
        sale.innerHTML = `<span class="sr-only">Sale price</span>${priceEl.textContent}`;
        if (!compare) {
          compare = document.createElement('compare-at-price');
          compare.className = 'h7 line-through';
          compare.setAttribute('data-sub-compare', '');
          sale.after(compare);
        }
        compare.textContent = this.getAttribute('onetime-price') || '';
      } else {
        sale.innerHTML = sale.dataset.onetimeHtml;
        if (compare) compare.remove();
      }
    }
  }

  customElements.define('subscription-picker', SubscriptionPicker);

  // Impact swaps re-renderable blocks after this event; give it a beat then re-apply
  document.addEventListener('variant:changed', () => {
    setTimeout(() => {
      document.querySelectorAll('subscription-picker').forEach((p) => p.render());
    }, 80);
  });
})();
